import { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";
import { collection, doc, getDoc, setDoc, updateDoc, deleteDoc, onSnapshot, query, orderBy, serverTimestamp } from "firebase/firestore";

function userCol(uid, col) { return collection(db, "users", uid, col); }
function userDoc(uid, col, id) { return doc(db, "users", uid, col, id); }

export function useCapital() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setEntries([]); setLoading(false); return; }
    const q = query(userCol(user.uid, "capital"), orderBy("date", "desc"));
    const unsub = onSnapshot(q, snap => { setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); });
    return unsub;
  }, [user]);

  const addEntry = useCallback(async (entry) => {
    if (!user) return;
    await setDoc(userDoc(user.uid, "capital", `cap_${Date.now()}`), { ...entry, created_at: serverTimestamp() });
  }, [user]);

  const deleteEntry = useCallback(async (id) => {
    if (!user) return;
    await deleteDoc(userDoc(user.uid, "capital", id));
  }, [user]);

  const totalCapital = entries.reduce((sum, e) => sum + (e.type === "deposit" ? e.amount : -e.amount), 0);
  return { entries, loading, addEntry, deleteEntry, totalCapital };
}

export function useTrades() {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setTrades([]); setLoading(false); return; }
    const q = query(userCol(user.uid, "trades"), orderBy("entry_date", "desc"));
    const unsub = onSnapshot(q, snap => { setTrades(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); });
    return unsub;
  }, [user]);

  const addTrade = useCallback(async (trade) => {
    if (!user) return;
    const id = `trade_${Date.now()}`;
    await setDoc(userDoc(user.uid, "trades", id), { ...trade, status: "open", created_at: serverTimestamp() });
    return id;
  }, [user]);

  const updateTrade = useCallback(async (id, updates) => {
    if (!user) return;
    await updateDoc(userDoc(user.uid, "trades", id), updates);
  }, [user]);

  const deleteTrade = useCallback(async (id) => {
    if (!user) return;
    await deleteDoc(userDoc(user.uid, "trades", id));
  }, [user]);

  return { trades, loading, addTrade, updateTrade, deleteTrade };
}

export function useSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({ capital: 5000000, risk_percent: 0.8, broker: "Zerodha", t1_pct: 40, t2_pct: 40, remainder_pct: 20 });

  useEffect(() => {
    if (!user) return;
    getDoc(userDoc(user.uid, "settings", "prefs")).then(d => { if (d.exists()) setSettings(s => ({ ...s, ...d.data() })); });
  }, [user]);

  const saveSettings = useCallback(async (s) => {
    if (!user) return;
    await setDoc(userDoc(user.uid, "settings", "prefs"), s);
    setSettings(s);
  }, [user]);

  return { settings, saveSettings };
}
